import { SortDirection } from "./sortOptions";

// The comparator every leads board sorts its rows with. Beside sortOptions.ts
// because the two halves belong together: that file names the sorts, this one
// performs them, and they were shared while the sort itself was triplicated
// across the inbox, the results list and the saved-searches list.
//
// The rule is value, then confidence, then a stable last key, and both
// tie-breaks are load-bearing. Every ranked column on these boards is a rounded
// percentage or a five-value band, so ties are the common case rather than the
// exception - and with no last rule the order inside a tie is whatever the read
// happened to return, which differs between reads and reshuffles rows under an
// operator working down the list.
export interface SortRules<T> {
  dir: SortDirection;
  // The column being sorted on.
  valueFor: (row: T) => string | number;
  // Confidence, when the sorted column is the coarse lead-value band: four
  // values over a whole board leaves the order inside a band meaningless
  // otherwise. Null for every other column, where the value IS the ranking.
  // Follows `dir`, because it is a continuation of the operator's ranking.
  confidenceTieBreak: ((row: T) => number) | null;
  // The stable last key - the address on the two lead boards, the name on the
  // saved-searches list. Always ASCENDING, never mirrored by `dir`: it is a
  // stable ordering, not a second thing the operator chose to rank on. Null
  // when it IS the sorted column, where it would be a no-op.
  finalTieBreak: ((row: T) => string) | null;
}

export const bySortRules =
  <T>({
    dir,
    valueFor,
    confidenceTieBreak,
    finalTieBreak,
  }: SortRules<T>): ((a: T, b: T) => number) =>
  (a, b) => {
    const sign = dir === "asc" ? 1 : -1;
    const av = valueFor(a);
    const bv = valueFor(b);
    if (av < bv) return -sign;
    if (av > bv) return sign;
    if (confidenceTieBreak) {
      const ac = confidenceTieBreak(a);
      const bc = confidenceTieBreak(b);
      if (ac !== bc) return ac < bc ? -sign : sign;
    }
    if (!finalTieBreak) return 0;
    const ax = finalTieBreak(a);
    const bx = finalTieBreak(b);
    return ax < bx ? -1 : ax > bx ? 1 : 0;
  };
